import React from 'react'
import { Alert } from 'react-bootstrap'
import FileContainer from '../FileContainer/FileContainer'
import { formatFileSize } from '../../constants/fileTypes'

// New attachment being uploaded
export interface NewAttachment {
  file: File
  filename: string
  size: number
}

// Existing attachment from server
export interface ExistingAttachment {
  id: number
  filename: string
  size: number
  file_url: string
}

// Union type for all attachments
export type Attachment = NewAttachment | ExistingAttachment

// Type guard functions
export function isNewAttachment(
  attachment: Attachment
): attachment is NewAttachment {
  return 'file' in attachment
}

export function isExistingAttachment(
  attachment: Attachment
): attachment is ExistingAttachment {
  return 'id' in attachment && 'file_url' in attachment
}

interface Props {
  attachments: Attachment[]
  onRemove?: (index: number) => void
  disabled?: boolean
  maxSize?: number // Max total size in bytes
  warningThreshold?: number // Show warning when size exceeds this (in bytes)
  showSummary?: boolean // Show file count and total size
}

export const AttachmentList: React.FC<Props> = ({
  attachments,
  onRemove,
  disabled = false,
  maxSize,
  warningThreshold,
  showSummary = true,
}) => {
  if (attachments.length === 0) return null

  // Calculate total size
  const totalSize = attachments.reduce((sum, att) => sum + att.size, 0)
  const isNearLimit =
    warningThreshold && maxSize ? totalSize > warningThreshold : false

  return (
    <div className="attachment-list mt-2">
      {showSummary && (
        <div className="d-flex align-items-center justify-content-between mb-2">
          <small className="text-muted fw-medium">
            {attachments.length} file(s) attached
          </small>
          <small className="text-muted">{formatFileSize(totalSize)}</small>
        </div>
      )}

      {isNearLimit && maxSize && (
        <Alert variant="warning" className="py-2 px-3 mb-2">
          <small>
            ⚠️ Total size {formatFileSize(totalSize)} approaching{' '}
            {formatFileSize(maxSize)} limit
          </small>
        </Alert>
      )}

      <div className="d-flex flex-column gap-2">
        {attachments.map((attachment, index) => (
          <FileContainer
            key={index}
            file={{ name: attachment.filename }}
            handleRemoveFile={
              disabled || !onRemove ? undefined : () => onRemove(index)
            }
            href={
              isExistingAttachment(attachment) ? attachment.file_url : undefined
            }
          />
        ))}
      </div>
    </div>
  )
}
